import type { ComponentProps } from 'react';
import { Skeleton as AntdSkeletonBase } from 'antd';

/**
 * antd 6 routes `className` to the wrapper `<div>` and exposes the inner
 * visible span only via the new `classNames.content` slot. Sizing/shape
 * utilities (`w-*`, `h-*`, `min-*`, `max-*`, `rounded-*`) need to land on
 * that inner span; layout utilities (margin, padding, etc.) belong on the
 * wrapper. We split the user-supplied `className` automatically so call
 * sites stay in the antd-5-style `<AntdSkeleton.Input className="w-full h-[92px]" />` form.
 *
 * Also defeats antd 6's `min-inline-size: 100%` on the inner so a smaller
 * `w-2`/`w-[10px]` actually shrinks instead of being clamped to wrapper width.
 */
const INNER_TOKEN_RE = /^(?:[\w-]+:)*(?:(?:min-|max-)?[wh]-|rounded(?:-|$))/;

const splitClasses = (className?: string) => {
  if (!className) return { wrapper: undefined, inner: undefined };
  const tokens = className.split(/\s+/).filter(Boolean);
  const inner = tokens.filter((tok) => INNER_TOKEN_RE.test(tok));
  const wrapper = tokens.filter((tok) => !INNER_TOKEN_RE.test(tok));
  return {
    wrapper: wrapper.length ? wrapper.join(' ') : undefined,
    inner: inner.length ? inner.join(' ') : undefined,
  };
};

// Capture the originals BEFORE we build our namespace. Otherwise our wrapper
// would call into itself (we expose `AntdSkeleton` whose `.Input` is our
// SkeletonInput; rendering `AntdSkeletonBase.Input` after the assign would
// resolve to the same wrapper and recurse).
const OriginalInput = AntdSkeletonBase.Input;
const OriginalButton = AntdSkeletonBase.Button;

type AntdSkeletonInputProps = ComponentProps<typeof OriginalInput>;
type AntdSkeletonButtonProps = ComponentProps<typeof OriginalButton>;

const SkeletonInput = ({ className, classNames, ...rest }: AntdSkeletonInputProps) => {
  const { wrapper, inner } = splitClasses(className);
  return (
    <OriginalInput
      {...rest}
      className={wrapper}
      classNames={{
        ...classNames,
        content: ['min-w-0 min-h-0', inner, classNames?.content].filter(Boolean).join(' '),
      }}
    />
  );
};
SkeletonInput.displayName = 'AntdSkeleton.Input';

const SkeletonButton = ({ className, classNames, ...rest }: AntdSkeletonButtonProps) => {
  const { wrapper, inner } = splitClasses(className);
  return (
    <OriginalButton
      {...rest}
      className={wrapper}
      classNames={{
        ...classNames,
        content: ['min-w-0 min-h-0', inner, classNames?.content].filter(Boolean).join(' '),
      }}
    />
  );
};
SkeletonButton.displayName = 'AntdSkeleton.Button';

// Build a fresh namespace object instead of mutating antd's. The base export
// is callable (the parent `<Skeleton>` component) and has Avatar/Image/Node/
// Title/Paragraph siblings we want to preserve.
type AntdSkeletonNamespace = typeof AntdSkeletonBase & {
  Button: typeof SkeletonButton;
  Input: typeof SkeletonInput;
};

export const AntdSkeleton = new Proxy(AntdSkeletonBase, {
  get(target, prop, receiver) {
    if (prop === 'Input') return SkeletonInput;
    if (prop === 'Button') return SkeletonButton;
    return Reflect.get(target, prop, receiver);
  },
}) as AntdSkeletonNamespace;
